import React, { useState, useEffect, useCallback } from 'react';
import { GitHubClient } from './services/githubClient';
import { GitHubTokenInput } from './components/GitHubTokenInput';
import { DualTagSelector } from './components/DualTagSelector';
import { CommitsTable } from './components/CommitsTable';
import { Sidebar } from './components/Sidebar';
import { LoadingSpinner, ErrorState } from './components/LoadingSpinner';
import { GitHubTag, FormattedCommit } from './types';
import { formatDateForDisplay } from './utils/dateUtils';
import './App.css';



function App() {
  const [githubToken, setGithubToken] = useState('');
  const [repoOwner, setRepoOwner] = useState('');
  const [repoName, setRepoName] = useState('');
  const [ticketSource, setTicketSource] = useState('linear');
  const [ticketWorkspace, setTicketWorkspace] = useState('');
  const [tags, setTags] = useState<GitHubTag[] | null>(null);
  const [selectedFromTag, setSelectedFromTag] = useState<GitHubTag | null>(null);
  const [selectedToTag, setSelectedToTag] = useState<GitHubTag | null>(null);
  const [commits, setCommits] = useState<FormattedCommit[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [client, setClient] = useState<GitHubClient | null>(null);

  // Initialize GitHub client when token or repo info changes
  useEffect(() => {
    if (githubToken && repoOwner && repoName && ticketWorkspace) {
      setClient(new GitHubClient(githubToken, ticketSource, ticketWorkspace));
      setTags(null); // Reset tags when configuration changes
      setError(null);
    } else {
      setClient(null);
      setTags(null);
      setSelectedFromTag(null);
      setSelectedToTag(null);
      setCommits(null);
    }
  }, [githubToken, repoOwner, repoName, ticketSource, ticketWorkspace]);

  // Load tags when client is available
  useEffect(() => {
    if (client && !tags) {
      loadTags();
    }
  }, [client, tags]);

  const loadTags = async () => {
    if (!client) return;

    setIsLoading(true);
    setError(null);

    try {
      const fetchedTags = await client.getRepositoryTags(repoOwner, repoName);
      if (fetchedTags && fetchedTags.length > 0) {
        setTags(fetchedTags);
        console.log(fetchedTags);
      } else {
        setError('Failed to fetch repository tags');
      }
    } catch (err) {
      setError(`Error fetching tags: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTagSelection = useCallback((fromTag: GitHubTag | null, toTag: GitHubTag | null) => {
    // Check if tags actually changed
    const tagsChanged = (
      selectedFromTag?.name !== fromTag?.name ||
      selectedToTag?.name !== toTag?.name
    );

    if (tagsChanged) {
      setSelectedFromTag(fromTag);
      setSelectedToTag(toTag);
      setCommits(null); // Reset commits when tags change
    }
  }, [selectedFromTag, selectedToTag]);

  // Load commits when both tags are selected and changed
  useEffect(() => {
    if (client && selectedFromTag && selectedToTag && !commits) {
      loadCommits();
    }
  }, [client, selectedFromTag, selectedToTag, commits]);

  const loadCommits = async () => {
    if (!client || !selectedFromTag || !selectedToTag) return;

    setIsLoading(true);
    setError(null);

    try {
      const rawCommits = await client.getCommitsBetweenTags(
        repoOwner,
        repoName,
        selectedFromTag.commit.sha,
        selectedToTag.commit.sha
      );

      const formattedCommits = rawCommits.map(commit => client.formatCommitForDisplay(commit));
      setCommits(formattedCommits);

      if (formattedCommits.length === 0) {
        setError('No commits found between the selected tags.');
      }
    } catch (err) {
      setError(`Error fetching commits: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    setTags(null);
    setSelectedFromTag(null);
    setSelectedToTag(null);
    setCommits(null);
    setError(null);
  };

  const isConfigured = !!(githubToken && repoOwner && repoName && ticketWorkspace && (githubToken.startsWith('ghp_') || githubToken.startsWith('github_pat_')));

  return (
    <div className="app">
      <div className="app-layout">
        <Sidebar isConfigured={isConfigured} repoOwner={repoOwner} repoName={repoName} />
        
        <div className="main-content">
          <div className="header">
            <h1>🎫 Ticket Tracker</h1>
            <p>Track tickets and browse tags and commits from any GitHub repository</p>
          </div>

          <GitHubTokenInput 
            onTokenChange={setGithubToken}
            onOwnerChange={setRepoOwner}
            onRepoChange={setRepoName}
            onTicketSourceChange={setTicketSource}
            onTicketWorkspaceChange={setTicketWorkspace}
          />

          {!isConfigured ? (
            <div style={{ color: '#007bff', padding: '20px', textAlign: 'center' }}>
              👆 Please enter your GitHub configuration to continue.
            </div>
          ) : (
            <>
              {error && <ErrorState error={error} />}
              
              {isLoading && <LoadingSpinner message="🔄 Fetching repository data..." />}

              {tags && !isLoading && (
                <>
                  <DualTagSelector
                    tags={tags}
                    onTagSelection={handleTagSelection}
                    selectedFromTag={selectedFromTag}
                    selectedToTag={selectedToTag}
                  />

                  {selectedFromTag && selectedToTag && (
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '1fr 1fr', 
                      gap: '20px', 
                      margin: '20px 0',
                      padding: '15px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px',
                      border: '1px solid #dee2e6'
                    }}>
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{selectedToTag.name} <code>{selectedToTag.sha?.substring(0, 7)}</code></div>
                        <div style={{ color: '#666', fontSize: '14px' }}>📅 {formatDateForDisplay(selectedToTag.date)}</div>
                      </div>
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{selectedFromTag.name} <code>{selectedFromTag.sha?.substring(0, 7)}</code></div>
                        <div style={{ color: '#666', fontSize: '14px' }}>📅 {formatDateForDisplay(selectedFromTag.date)}</div>
                      </div>
                    </div>
                  )}

                  {commits && (
                    <>
                      <div style={{ 
                        color: '#28a745', 
                        backgroundColor: '#d4edda',
                        border: '1px solid #c3e6cb',
                        borderRadius: '4px',
                        padding: '10px',
                        marginBottom: '20px'
                      }}>
                        ✅ Found {commits.length} commits added between {selectedFromTag?.name} and {selectedToTag?.name}
                      </div>
                      <CommitsTable commits={commits} />
                    </>
                  )}

                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    marginTop: '30px',
                    paddingTop: '20px',
                    borderTop: '1px solid #dee2e6'
                  }}>
                    <button
                      onClick={handleRefresh}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }}
                    >
                      🔄 Refresh Data
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;